// 鶏 (inverted-jester) の仮の FX を本番の演出にする演出 (2026-10-31・10-31 作者の答え A で配布版の既定・DEV ?roosterFx=now で前の見た目)。
//   記録 = docs/research/2026-10-31-rooster-fx.md。
//   作者 (10-29 介錯): 線や円を引いただけの「コードで描いた感」は避ける → 線の輪・枠・光の柱を使わず、
//   羽根は鶏の絵と同じ塗り (2色のべた塗り＋細い暗い縁) の形、光はにじむ明るさ、土ぼこりは描いた絵 (dust_land) で出す。
//   色: 羽根の床技は弾ける＝白。赤は弾けない技だけ (鳴き挟みの締めの目の光)。
//   羽根・綿毛・羽根の土ぼこりは描いた絵 (10-31・票 rooster-feather-fx-1031)。羽根の色は鶏の本体の羽に揃える (10-31 作者の答え fix)。
import type { ActiveHit, Boss } from './types'
import { drawMat, dustReady, dustSprite, tinted } from './exec_fx_mats'
import { ROOSTER_RUSH } from './rooster_rush'

export type RoosterFxLook = 'now' | 'v1'
let look: RoosterFxLook = 'v1' // 10-31 作者の答え A で配布版へ (?roosterFx=now で前の見た目)
export function setRoosterFx(v: string | null | undefined): RoosterFxLook {
  look = v === 'now' ? 'now' : 'v1'
  return look
}
export function roosterFx(): RoosterFxLook { return look }

// 羽根の絵 (10-31・Codex 票 rooster-feather-fx-1031)。
//   10-31 作者 (答えるページ ADTcniTeTrt4hN1WrG5F3a・choice=fix)「羽の色を鶏本体の色と統一して」→ 既定 = 本体の羽の色にした絵 (art)。
//   前の版 = DEV ?roosterFeather=white (Codex の白い羽根のまま)・?roosterFeather=code (コードの葉形)。
//   絵の元 = そざい/concept/rooster-fx-1031/sheets/・切り出し = scripts/slice_rooster_fx.py → public/fx/rooster/feather_white_<i>.png
//   → 本体の羽の色へ = scripts/recolor_rooster_feathers.py → feather_<i>.png (影の紺 40,40,54・明るい所の青灰 114,120,153)。
//   feather_0..3 = 羽根 (0,1 長い風切羽・2,3 短い羽。色付き・根元の軸の先 = 下辺の真ん中)。
//   fluff_0..3 = 綿毛 (白＋アルファ・中心が真ん中)・dust_0..3 = 刺さった所の土ぼこり (白＋アルファ・根元 = 下辺の真ん中)。
//   絵が読めていない間 (読み込み中・Vitest) はコードの形で描く。
export type RoosterFeatherLook = 'code' | 'art' | 'white'
let featherLook: RoosterFeatherLook = 'art'
const ART_KINDS = ['feather', 'fluff', 'dust'] as const
type ArtKind = typeof ART_KINDS[number]
const art: Record<ArtKind, (HTMLImageElement | null)[]> = { feather: [null, null, null, null], fluff: [null, null, null, null], dust: [null, null, null, null] }
let artStarted = false
function preloadFeatherArt(): void {
  if (artStarted || typeof Image === 'undefined') return
  artStarted = true
  for (const kind of ART_KINDS) {
    art[kind].forEach((_, i) => {
      const img = new Image()
      img.onload = () => { art[kind][i] = img }
      img.src = `/fx/rooster/${kind === 'feather' && featherLook === 'white' ? 'feather_white' : kind}_${i}.png`
    })
  }
}
export function setRoosterFeather(v: string | null | undefined): RoosterFeatherLook {
  featherLook = v === 'code' ? 'code' : v === 'white' ? 'white' : 'art'
  if (featherLook !== 'code') preloadFeatherArt()
  return featherLook
}
export function roosterFeather(): RoosterFeatherLook { return featherLook }
function artOf(kind: ArtKind, i: number): HTMLImageElement | null {
  if (featherLook === 'code' || art[kind].some(x => x === null)) return null
  return art[kind][((Math.floor(i) % 4) + 4) % 4]!
}

/** 暁呼びの羽根の床技か (AttackDef id = src/rooster_vertical.ts roosterFeatherAttack)。 */
export function isRoosterFeatherHit(hz: Pick<ActiveHit, 'attackId'>): boolean {
  return hz.attackId === 'roosterFeather'
}

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v))
// 同じ羽根は毎フレーム同じ揺れ方にする (hit id から決める乱数)。
function rand(seed: number, k: number): number {
  const s = Math.sin(seed * 127.1 + k * 311.7) * 43758.5453
  return s - Math.floor(s)
}

// 羽根の色 (鶏の絵の羽根の塗りに寄せた、白い夜明けの羽根)。
const FEATHER_LIGHT = '#eef1f6'
const FEATHER_SHADE = '#a9b3c4'
const FEATHER_EDGE = 'rgba(34,38,50,0.85)'
// 描いた羽根の高さ = len × これ (コードの葉形は軸の先から根元まで len)。
const FEATHER_ART_SCALE = 1.1

/**
 * 羽根1枚。(x, y) = 羽根の先 (床に刺さる側)、rot = 先から根元への向き (0 = 真上)。
 * 羽根は葉の形の羽で、軸を境に片側を影の色にし、縁に2か所の切れ込みを入れる (幾何の形に見せない)。
 */
export function drawFeather(
  ctx: CanvasRenderingContext2D, x: number, y: number, len: number, rot: number, alpha: number, seed = 0,
): void {
  if (alpha <= 0.01 || len < 2) return
  // 描いた羽根: 大きい羽根 (len 40 以上) は長い風切羽、小さい羽根は短い羽。
  const img = artOf('feather', len >= 40 ? Math.floor(rand(seed, 5) * 2) : 2 + Math.floor(rand(seed, 5) * 2))
  if (img) {
    const h = len * FEATHER_ART_SCALE
    drawMat(ctx, img, x, y, h * (img.naturalWidth / img.naturalHeight), h, 0.5, 1, alpha, rot)
    return
  }
  const w = len * 0.2
  const nA = 0.42 + rand(seed, 1) * 0.12   // 切れ込みの位置 (先からの割合)
  const nB = 0.62 + rand(seed, 2) * 0.12
  ctx.save()
  ctx.globalAlpha = Math.min(1, alpha)
  ctx.translate(x, y)
  ctx.rotate(rot)
  // 羽 (先 = (0,0)、根元 = (0,-len))。左半分 = 明るい色、右半分 = 影の色。
  const half = (side: 1 | -1, fill: string): void => {
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.quadraticCurveTo(side * w * 0.9, -len * 0.18, side * w, -len * nA)
    ctx.lineTo(side * w * 0.55, -len * (nA + 0.04))
    ctx.quadraticCurveTo(side * w * 1.05, -len * (nA + 0.1), side * w * 0.95, -len * nB)
    ctx.lineTo(side * w * 0.5, -len * (nB + 0.05))
    ctx.quadraticCurveTo(side * w * 0.85, -len * 0.82, 0, -len * 0.9)
    ctx.closePath()
    ctx.fillStyle = fill
    ctx.fill()
  }
  half(-1, FEATHER_LIGHT)
  half(1, FEATHER_SHADE)
  // 縁 (細い暗い線＝絵の縁取り。光らせない)
  ctx.lineWidth = Math.max(1, len * 0.018)
  ctx.lineJoin = 'round'
  ctx.strokeStyle = FEATHER_EDGE
  ctx.beginPath()
  for (const side of [-1, 1] as const) {
    ctx.moveTo(0, 0)
    ctx.quadraticCurveTo(side * w * 0.9, -len * 0.18, side * w, -len * nA)
    ctx.lineTo(side * w * 0.55, -len * (nA + 0.04))
    ctx.quadraticCurveTo(side * w * 1.05, -len * (nA + 0.1), side * w * 0.95, -len * nB)
    ctx.lineTo(side * w * 0.5, -len * (nB + 0.05))
    ctx.quadraticCurveTo(side * w * 0.85, -len * 0.82, 0, -len * 0.9)
  }
  ctx.stroke()
  // 軸 (羽の先から根元の少し先まで)
  ctx.strokeStyle = 'rgba(70,78,96,0.9)'
  ctx.lineWidth = Math.max(1, len * 0.022)
  ctx.beginPath()
  ctx.moveTo(0, len * 0.02)
  ctx.lineTo(0, -len)
  ctx.stroke()
  ctx.restore()
}

// 床にたまる夜明けの明るさ (枠も輪も引かない。両端はにじませて消す)。
function drawDawnPool(ctx: CanvasRenderingContext2D, x0: number, x1: number, floorY: number, a: number, shaftH: number): void {
  if (a <= 0.01) return
  const w = x1 - x0
  const soft = Math.min(36, w * 0.3)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  // 上から差す淡い明るさ (範囲を立って読ませる。上へ行くほど消える)
  const v = ctx.createLinearGradient(0, floorY - shaftH, 0, floorY)
  v.addColorStop(0, 'rgba(255,246,228,0)')
  v.addColorStop(1, `rgba(255,246,228,${(a * 0.32).toFixed(3)})`)
  // 横の両端は帯を細切れに重ねて、なめらかに消す。
  const steps = 10
  for (let i = 0; i < steps; i++) {
    const fx0 = x0 - soft + ((w + soft * 2) * i) / steps
    const fx1 = x0 - soft + ((w + soft * 2) * (i + 1)) / steps
    const mid = (fx0 + fx1) / 2
    const edge = Math.min(clamp01((mid - (x0 - soft)) / (soft * 2)), clamp01((x1 + soft - mid) / (soft * 2)))
    ctx.globalAlpha = edge * edge * (3 - 2 * edge)
    ctx.fillStyle = v
    ctx.fillRect(fx0, floorY - shaftH, fx1 - fx0 + 0.5, shaftH)
  }
  // 床に溜まる明るさ (横長のにじむ楕円)
  ctx.globalAlpha = 1
  const cx = (x0 + x1) / 2
  const rx = w / 2 + soft
  ctx.translate(cx, floorY)
  ctx.scale(1, 14 / rx)
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx)
  g.addColorStop(0, `rgba(255,250,238,${(a * 0.85).toFixed(3)})`)
  g.addColorStop(0.7, `rgba(240,236,226,${(a * 0.5).toFixed(3)})`)
  g.addColorStop(1, 'rgba(230,226,216,0)')
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(0, 0, rx, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

const FALL_PX = 420
// 落ちてくる羽根の位置 (予兆の進み p = 0..1。p = 1 でちょうど床に刺さる)。
function fallingFeather(hz: ActiveHit, p: number): { x: number; y: number; rot: number } {
  const cx = (hz.xStart + hz.xEnd) / 2
  const sway = Math.sin(p * Math.PI * 2.2 + rand(hz.id, 3) * 6) * 16 * (1 - p)
  const e = p * p   // 初めはふわりと、終わりは速く
  return { x: cx + sway, y: FALL_PX * e, rot: (sway / 16) * 0.35 + 0.18 * (1 - p) }
}

/** 予兆: 白い羽根が上から舞い落ち、落ちる範囲の床に夜明けの明るさがたまる。 */
export function drawRoosterFeatherTelegraph(ctx: CanvasRenderingContext2D, hz: ActiveHit, floorY: number): void {
  const p = clamp01(hz.elapsedMs / hz.telegraphMs)
  drawDawnPool(ctx, hz.xStart, hz.xEnd, floorY, 0.18 + 0.42 * p, 90 + 70 * p)
  const f = fallingFeather(hz, p)
  const len = 64
  // 羽根の先が下を向いて落ちる (rot ≒ 0)。先の y = 床 - 落ちる残り。
  drawFeather(ctx, f.x, floorY - FALL_PX + f.y, len, f.rot, clamp01(p * 4), hz.id)
  // 小さな羽根2枚 (遅れてひらひら・床までは来ない)
  for (const k of [0, 1]) {
    const side = k === 0 ? -1 : 1
    const q = clamp01(p * 0.75)
    const sx = (hz.xStart + hz.xEnd) / 2 + side * (hz.xEnd - hz.xStart) * 0.28 + Math.sin(p * 7 + k * 2) * 10
    const sy = floorY - FALL_PX * 0.95 + FALL_PX * 0.7 * q * q
    drawFeather(ctx, sx, sy, 26, side * 0.6 + Math.sin(p * 9 + k) * 0.5, clamp01(p * 3) * (1 - clamp01((p - 0.85) / 0.15)), hz.id + k + 1)
  }
}

// 刺さった羽根 (当たりの間と、その後しばらく)。
const STUCK_ROT = -0.22
const LINGER_MS = 650
type Stuck = { id: number; x: number; endT: number; startT: number }
const stuck = new Map<number, Stuck>()

// 綿毛 (描いた絵があれば綿毛・なければ小さな羽根)。色は羽根と同じく本体の羽の明るい青灰 (10-31・前の版 white は白)。
const FLUFF_RGB = '150,156,186'
function drawFluff(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rot: number, alpha: number, seed: number): void {
  const img = artOf('fluff', Math.floor(rand(seed, 6) * 4))
  if (!img) { drawFeather(ctx, x, y, size, rot, alpha, seed); return }
  const s = size * 1.3
  drawMat(ctx, tinted(img, featherLook === 'white' ? '244,244,248' : FLUFF_RGB), x, y, s, s, 0.5, 0.5, alpha, rot)
}

function drawImpact(ctx: CanvasRenderingContext2D, x: number, floorY: number, w: number, ms: number, seed: number): void {
  // 刺さった瞬間の床の明るさ (すぐ引く)
  const flash = 1 - clamp01(ms / 140)
  if (flash > 0) drawDawnPool(ctx, x - w / 2, x + w / 2, floorY, 0.9 * flash, 40)
  // 床を這って左右へ広がる土ぼこり (描いた絵・速く平たく)
  if (artOf('dust', 0)) {
    // 羽根の絵の比較版: 刺さった所から立つ小さく低い土ぼこり (4コマ)。
    const k = clamp01(ms / 300)
    const img = artOf('dust', Math.min(3, Math.floor(k * 4)))!
    const dw = w * (1.3 + 0.3 * k)
    drawMat(ctx, tinted(img, '238,236,230'), x, floorY + 4, dw, dw * (img.naturalHeight / img.naturalWidth), 0.5, 1, 1 - k * k)
  } else if (dustReady()) {
    const k = clamp01(ms / 260)
    const img = dustSprite('land', Math.min(3, Math.floor(k * 4)))
    if (img) {
      const a = (1 - k) * 0.85
      const dw = w * (1.05 + 0.5 * k)
      drawMat(ctx, tinted(img, '238,236,230'), x, floorY + 4, dw, dw * 0.32, 0.5, 1, a)
    }
  }
  // 綿毛 (小さな羽根がふわっと散る)
  const life = 520
  if (ms < life) {
    const q = ms / life
    for (let i = 0; i < 7; i++) {
      const dir = (i % 2 === 0 ? -1 : 1) * (0.35 + rand(seed, 10 + i) * 0.9)
      const sp = 120 + rand(seed, 20 + i) * 120
      const px = x + dir * sp * (1 - (1 - q) * (1 - q)) * 0.9
      const py = floorY - 8 - (70 + rand(seed, 30 + i) * 60) * Math.sin(Math.PI * Math.min(1, q * 1.3)) * (1 - q * 0.5)
      drawFluff(ctx, px, py, 12 + rand(seed, 40 + i) * 8, rand(seed, 50 + i) * 6 + q * 3 * dir, (1 - q) * 0.95, seed + 7 + i)
    }
  }
}

/** 当たりの間: 羽根が斜めに床へ刺さり、床が一瞬明るみ、土ぼこりと綿毛が散る。 */
export function drawRoosterFeatherActive(ctx: CanvasRenderingContext2D, hz: ActiveHit, t: number, floorY: number): void {
  const cx = (hz.xStart + hz.xEnd) / 2
  const ms = hz.elapsedMs - hz.telegraphMs
  const rec = stuck.get(hz.id)
  if (!rec || t < rec.startT) stuck.set(hz.id, { id: hz.id, x: cx, startT: t - ms / 1000, endT: t })
  else rec.endT = t
  drawImpact(ctx, cx, floorY, hz.xEnd - hz.xStart, ms, hz.id)
  // 刺さった直後に少しめり込む
  const sink = 6 + 6 * clamp01(ms / 60)
  drawFeather(ctx, cx, floorY + sink, 64, STUCK_ROT, 1, hz.id)
}

/** 当たりが消えた後: 刺さった羽根を残してから薄れさせる (hits から消えた分だけ描く)。 */
export function drawRoosterFeatherLinger(ctx: CanvasRenderingContext2D, t: number, hits: readonly ActiveHit[], floorY: number): void {
  if (stuck.size === 0) return
  for (const rec of [...stuck.values()]) {
    if (t < rec.startT || t - rec.endT > LINGER_MS / 1000) { stuck.delete(rec.id); continue }
    if (hits.some(h => h.id === rec.id)) continue
    const age = (t - rec.endT) * 1000
    const ms = (t - rec.startT) * 1000
    drawImpact(ctx, rec.x, floorY, 120, ms, rec.id)
    const a = 1 - clamp01((age - LINGER_MS * 0.4) / (LINGER_MS * 0.6))
    drawFeather(ctx, rec.x, floorY + 12, 64, STUCK_ROT, a, rec.id)
  }
}

/**
 * 降りる直前の目の光 (宙廻り・鳴き挟み)。輪や十字の線を引かず、目の所ににじむ光と横に流れる淡いにじみで出す。
 * 白 = 弾ける / 赤 = 弾けない締め。
 */
export function drawRoosterEyeGlint(
  ctx: CanvasRenderingContext2D, x: number, y: number, progress: number, color: 'white' | 'red',
): void {
  const p = clamp01(progress)
  const grow = clamp01(p / 0.25)
  const fade = 1 - clamp01((p - 0.8) / 0.2)
  const a = grow * fade
  if (a <= 0.01) return
  const rgb = color === 'red' ? '255,74,74' : '255,250,236'
  const core = color === 'red' ? '255,214,206' : '255,255,255'
  const pulse = 1 + 0.08 * Math.sin(p * Math.PI * 6)
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  // まわりのにじみ
  const r = 30 * (0.6 + 0.4 * grow) * pulse
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, `rgba(${core},${(0.95 * a).toFixed(3)})`)
  g.addColorStop(0.25, `rgba(${rgb},${(0.6 * a).toFixed(3)})`)
  g.addColorStop(1, `rgba(${rgb},0)`)
  ctx.fillStyle = g
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fill()
  // 横に流れる淡いにじみ (線でなく、平たく潰したにじみ)
  ctx.translate(x, y)
  ctx.scale(1, 0.09)
  const rx = 78 * (0.5 + 0.5 * grow)
  const h = ctx.createRadialGradient(0, 0, 0, 0, 0, rx)
  h.addColorStop(0, `rgba(${core},${(0.85 * a).toFixed(3)})`)
  h.addColorStop(0.35, `rgba(${rgb},${(0.35 * a).toFixed(3)})`)
  h.addColorStop(1, `rgba(${rgb},0)`)
  ctx.fillStyle = h
  ctx.beginPath()
  ctx.arc(0, 0, rx, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

// 宙から降りて床を踏んだ時の土ぼこりと散る羽根。床を離れてからの一番高い所が 120px 以上で、高さが 0 になった瞬間を拾う
// (前のフレームだけを見ると、速く落ちる技は 0 の直前が 30px ほどで拾えない。時ずらしの小さな跳ね 56px は拾わない)。
type Stomp = { x: number; t0: number; seed: number }
const STOMP_MIN_PEAK = 120
let peakAlt = 0
let lastT = -1
let stomp: Stomp | null = null
const STOMP_MS = 700

/** 毎フレーム1回、鶏の本体を描く所で呼ぶ (床の影の後)。floorY = 影と同じ足元の高さ。 */
export function drawRoosterStompFx(ctx: CanvasRenderingContext2D, b: Boss, t: number, floorY: number): void {
  if (t < lastT) { stomp = null; peakAlt = 0 }
  const alt = b.altitudePx ?? 0
  if (alt <= 0.5) {
    if (peakAlt >= STOMP_MIN_PEAK) stomp = { x: b.x, t0: t, seed: Math.floor(t * 1000) % 997 }
    peakAlt = 0
  } else peakAlt = Math.max(peakAlt, alt)
  lastT = t
  if (!stomp) return
  const ms = (t - stomp.t0) * 1000
  if (ms > STOMP_MS) { stomp = null; return }
  const x = stomp.x
  // 床の一瞬の明るみ
  const flash = 1 - clamp01(ms / 120)
  if (flash > 0) drawDawnPool(ctx, x - 70, x + 70, floorY, 0.55 * flash, 24)
  // 左右へ速く平たく広がる土ぼこり (描いた絵を左右に1枚ずつ)
  if (dustReady()) {
    const k = clamp01(ms / 320)
    const img = dustSprite('land', Math.min(3, Math.floor(k * 4)))
    if (img) {
      const a = (1 - k) * 0.9
      const dw = 170 + 150 * (1 - (1 - k) * (1 - k))
      const src = tinted(img, '214,208,198')
      drawMat(ctx, src, x - 14, floorY + 4, dw, dw * 0.3, 1, 1, a)
      drawMat(ctx, src, x + 14, floorY + 4, dw, dw * 0.3, 1, 1, a, 0, true)
    }
  }
  // 抜けた羽根が数枚舞い上がって落ちる
  const q = ms / STOMP_MS
  for (let i = 0; i < 5; i++) {
    const dir = i % 2 === 0 ? -1 : 1
    const vx = dir * (60 + rand(stomp.seed, i) * 140)
    const vy = 260 + rand(stomp.seed, 10 + i) * 160
    const s = ms / 1000
    const px = x + vx * s
    const py = floorY - 20 - (vy * s - 520 * s * s)
    if (py > floorY) continue
    drawFeather(ctx, px, py, 20 + rand(stomp.seed, 20 + i) * 10, rand(stomp.seed, 30 + i) * 6 + s * 5 * dir, 1 - q * q, stomp.seed + i)
  }
}

// 弾くほど加速の合図 案A (DEV ?roosterRushCue=fluff・11-03・docs/research/2026-11-03-rooster-rush-cue.md)。
//   弾かれ・殴られて遅れが増えた時: 体から綿毛が2〜3つ抜けて落ちる (遅れが溜まった印)。
//   遅れを抱えたまま鬨ずらしの鳴きに入った時 (「カッ」と同じ瞬間): 遅れに比例した綿毛がぶわっと散る (急いた連の印)。
//   羽根 (feather) は暁呼びの床技の予告と同じ形なので使わず、綿毛だけ・床に届く前に消す。色は本体の羽の青灰 (FLUFF_RGB)。
type Puff = { x: number; y: number; t0: number; seed: number; n: number; big: boolean }
const PUFF_SMALL_MS = 650
const PUFF_BIG_MS = 820
const puffs: Puff[] = []
let rushLastT = -1
let rushPrevLag = 0
let rushPrevState: Boss['state'] | null = null

/** 毎フレーム1回、鶏の本体を描いた後に呼ぶ。footY = 足元 (宙にいれば宙の足元)・h = 絵の高さ。床 = b.y。 */
export function drawRoosterRushFluff(ctx: CanvasRenderingContext2D, b: Boss, t: number, footY: number, h: number): void {
  if (t < rushLastT) { puffs.length = 0; rushPrevLag = 0; rushPrevState = null }
  const lag = b.roosterRush?.lagMs ?? 0
  const bodyY = footY - h * 0.5
  const seed = Math.floor(t * 1000) % 997
  if (rushLastT >= 0 && b.state !== 'staggered') {
    if (lag > rushPrevLag + 1) puffs.push({ x: b.x, y: bodyY, t0: t, seed, n: 3, big: false })
    const head = b.state === 'telegraph' && rushPrevState !== 'telegraph' && lag >= ROOSTER_RUSH.tickCueMinLagMs
      && (b.currentAttack?.id === 'tokiZurashiShort' || b.currentAttack?.id === 'tokiZurashiLong')
    if (head) puffs.push({ x: b.x, y: bodyY, t0: t, seed: seed + 31, n: 5 + Math.round(9 * Math.min(1, lag / ROOSTER_RUSH.breakAtMs)), big: true })
  }
  rushLastT = t
  rushPrevLag = lag
  rushPrevState = b.state
  for (let k = puffs.length - 1; k >= 0; k--) {
    const p = puffs[k]!
    const life = p.big ? PUFF_BIG_MS : PUFF_SMALL_MS
    const ms = (t - p.t0) * 1000
    if (ms > life) { puffs.splice(k, 1); continue }
    const q = ms / life
    const s = ms / 1000
    for (let i = 0; i < p.n; i++) {
      const ang = (i / p.n) * Math.PI * 2 + rand(p.seed, i) * 0.9
      const sp = (p.big ? 170 : 70) + rand(p.seed, 10 + i) * (p.big ? 150 : 60)
      // 速く飛び出して空気で止まり、ゆっくり落ちる
      const go = (1 - Math.exp(-s * 6)) / 6
      const px = p.x + Math.cos(ang) * sp * go + (p.big ? 0 : (rand(p.seed, 20 + i) - 0.5) * 50)
      const py = p.y + Math.sin(ang) * sp * go * 0.7 + 60 * s * s + (p.big ? 0 : h * 0.15)
      if (py > b.y - 6) continue
      const size = (p.big ? 15 : 11) + rand(p.seed, 30 + i) * 8
      const a = (1 - q * q) * (q < 0.08 ? q / 0.08 : 1) * 0.95
      drawFluff(ctx, px, py, size, rand(p.seed, 40 + i) * 6 + s * 2, a, p.seed + i)
    }
  }
}

// 焦り狂いの段の合図 (11-04 から配布版・11-03 作者の答え cue=fluff を段に合わせて強くする・src/rooster_frenzy.ts)。
//   羽毛を逆立てる: 段1 = 首の飾り羽・段2 = 首と背・段3 = 全身と尾。段が上がるほど房が大きく、震えが細かい。
//   段が上がった瞬間 = 体から綿毛がぶわっと散る (「カッ」と同じ時)。下がった瞬間 = 逆立っていた房が抜けて落ちる (羽が寝る)。
//   息切れ = 嘴の先から小さな綿毛が上へ漂う。赤は使わない (赤は弾けない技だけ)・羽根の形は暁呼びの床技と同じなので使わない。
//   房の位置は立ち絵 (rooster_idle_1027・右向き) の首・背・尾を、足元からの高さ h の割合で置いた物 (絵が替わる技の最中は少しずれる)。
type Tuft = { fx: number; fy: number; nx: number; ny: number; stage: 1 | 2 | 3 }
const TUFTS: Tuft[] = [
  // 首の飾り羽 (首の後ろ側から頭の下)
  { fx: 0.10, fy: 0.74, nx: -0.6, ny: -0.8, stage: 1 }, { fx: 0.14, fy: 0.68, nx: -0.8, ny: -0.5, stage: 1 },
  { fx: 0.12, fy: 0.61, nx: -0.9, ny: -0.2, stage: 1 }, { fx: 0.20, fy: 0.64, nx: 0.2, ny: -0.9, stage: 1 },
  // 背
  { fx: -0.02, fy: 0.56, nx: -0.4, ny: -0.9, stage: 2 }, { fx: -0.12, fy: 0.52, nx: -0.5, ny: -0.85, stage: 2 },
  { fx: -0.20, fy: 0.47, nx: -0.7, ny: -0.7, stage: 2 },
  // 尾と胸・腹 (全身)
  { fx: -0.30, fy: 0.42, nx: -0.9, ny: -0.4, stage: 3 }, { fx: -0.34, fy: 0.30, nx: -1, ny: 0, stage: 3 },
  { fx: -0.28, fy: 0.20, nx: -0.8, ny: 0.4, stage: 3 }, { fx: 0.22, fy: 0.42, nx: 0.9, ny: -0.2, stage: 3 },
  { fx: 0.12, fy: 0.30, nx: 0.7, ny: 0.5, stage: 3 },
]
type FrenzyPuff = { x: number; y: number; t0: number; seed: number; n: number; kind: 'burst' | 'fall' | 'breath'; dir: number }
const frenzyPuffs: FrenzyPuff[] = []
let frenzyLastT = -1
let frenzyPrevStage = 0
let frenzyNextBreathT = 0

/** 毎フレーム1回、鶏の本体を描いた後に呼ぶ。stage = いまの段・breathing = 息切れの最中。footY = 足元・h = 絵の高さ。 */
export function drawRoosterFrenzyCue(ctx: CanvasRenderingContext2D, b: Boss, t: number, footY: number, h: number, stage: number, breathing: boolean): void {
  if (t < frenzyLastT) { frenzyPuffs.length = 0; frenzyPrevStage = 0; frenzyNextBreathT = 0 }
  const dir = b.facing === 'left' ? -1 : 1
  const seed = Math.floor(t * 1000) % 997
  if (frenzyLastT >= 0 && stage !== frenzyPrevStage) {
    if (stage > frenzyPrevStage) frenzyPuffs.push({ x: b.x, y: footY - h * 0.5, t0: t, seed, n: 6 + stage * 3, kind: 'burst', dir })
    else {
      // 逆立っていた房が抜けて落ちる (下がった段の房の数だけ)。
      for (const tf of TUFTS) {
        if (tf.stage <= frenzyPrevStage && tf.stage > stage) frenzyPuffs.push({ x: b.x + tf.fx * h * dir, y: footY - tf.fy * h, t0: t, seed: seed + Math.round(tf.fx * 100), n: 2, kind: 'fall', dir })
      }
    }
  }
  if (breathing && t >= frenzyNextBreathT) {
    frenzyPuffs.push({ x: b.x + 0.30 * h * dir, y: footY - 0.78 * h, t0: t, seed, n: 1, kind: 'breath', dir })
    frenzyNextBreathT = t + 0.32
  }
  frenzyLastT = t
  frenzyPrevStage = stage
  // 逆立った房。段が上がるほど大きく、細かく震える。崩れ・倒れの間と、宙にいる間 (縦の技は飛ぶ絵で、房の位置が合わない) は寝かせる。
  if (stage > 0 && b.state !== 'staggered' && (b.altitudePx ?? 0) < 6) {
    const size = 9 + stage * 3
    const hz = 5 + stage * 3
    for (let i = 0; i < TUFTS.length; i++) {
      const tf = TUFTS[i]!
      if (tf.stage > stage) continue
      const lift = h * (0.035 + 0.012 * stage) * (0.75 + 0.25 * Math.sin(t * Math.PI * 2 * hz + i * 1.7))
      const bx = b.x + tf.fx * h * dir
      const by = footY - tf.fy * h
      for (let k = 0; k < 2; k++) {
        const reach = lift * (0.55 + 0.6 * k)
        drawFluff(ctx, bx + tf.nx * reach * dir, by + tf.ny * reach, size * (1 - 0.18 * k), rand(i, k) * 6 + Math.sin(t * 9 + i) * 0.2, 0.9 - 0.15 * k, i * 7 + k)
      }
    }
  }
  for (let k = frenzyPuffs.length - 1; k >= 0; k--) {
    const p = frenzyPuffs[k]!
    const life = p.kind === 'burst' ? PUFF_BIG_MS : p.kind === 'fall' ? 900 : 700
    const ms = (t - p.t0) * 1000
    if (ms > life) { frenzyPuffs.splice(k, 1); continue }
    const q = ms / life
    const s = ms / 1000
    for (let i = 0; i < p.n; i++) {
      let px: number, py: number, size: number
      if (p.kind === 'burst') {
        const ang = (i / p.n) * Math.PI * 2 + rand(p.seed, i) * 0.9
        const sp = 190 + rand(p.seed, 10 + i) * 170
        const go = (1 - Math.exp(-s * 6)) / 6
        px = p.x + Math.cos(ang) * sp * go
        py = p.y + Math.sin(ang) * sp * go * 0.7 + 60 * s * s
        size = 15 + rand(p.seed, 30 + i) * 9
      } else if (p.kind === 'fall') {
        px = p.x + (rand(p.seed, i) - 0.5) * 30 + Math.sin(s * 5 + i) * 8
        py = p.y + 20 * s + 90 * s * s
        size = 12 + rand(p.seed, 30 + i) * 5
      } else {
        px = p.x + p.dir * 18 * s + Math.sin(s * 7) * 4
        py = p.y - 40 * s
        size = 8 + 6 * q
      }
      if (py > b.y - 6) continue
      const a = (1 - q * q) * (q < 0.08 ? q / 0.08 : 1) * (p.kind === 'breath' ? 0.7 : 0.95)
      drawFluff(ctx, px, py, size, rand(p.seed, 40 + i) * 6 + s * 2, a, p.seed + i)
    }
  }
}

/** テスト用: 覚えている刺さった羽根と踏み込みを捨てる。 */
export function resetRoosterFxMemory(): void {
  stuck.clear(); stomp = null; peakAlt = 0; lastT = -1
  puffs.length = 0; rushLastT = -1; rushPrevLag = 0; rushPrevState = null
  frenzyPuffs.length = 0; frenzyLastT = -1; frenzyPrevStage = 0; frenzyNextBreathT = 0
}
